import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Check, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, isHR } from '@/lib/auth';
import api from '@/lib/api';

export function MLPApplyCard() {
  const [eligibility, setEligibility] = useState(null);
  const [records, setRecords] = useState([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [elig, recs] = await Promise.all([
        api.get('/api/leave/mlp/check'),
        api.get('/api/leave/mlp/records'),
      ]);
      setEligibility(elig.data);
      setRecords(recs.data || []);
    } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleApply = async () => {
    if (!date) { toast.error('Select a date'); return; }
    setApplying(true);
    try {
      await api.post('/api/leave/mlp/apply', { date });
      toast.success('MLP leave applied & auto-approved');
      setDate('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply'); }
    setApplying(false);
  };

  if (loading) return <Skeleton className="h-40" />;
  if (!eligibility?.eligible) return null;

  return (
    <Card className="border-slate-200" data-testid="mlp-apply-card">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-pink-500" />
          <CardTitle className="text-sm font-semibold">MLP - Menstrual Leave</CardTitle>
          <Badge className={`ml-auto text-xs ${eligibility.remaining > 0 ? 'bg-pink-100 text-pink-700' : 'bg-slate-100 text-slate-500'}`}>
            {eligibility.remaining} of {eligibility.monthly_limit} remaining this month
          </Badge>
        </div>
        <p className="text-xs text-slate-400 mt-1">1 leave per month, auto-approved. No manager approval needed.</p>
      </CardHeader>
      <CardContent className="pt-2">
        {eligibility.remaining > 0 ? (
          <div className="flex items-end gap-3 mb-4">
            <div className="flex-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="mlp-date-input" />
            </div>
            <Button onClick={handleApply} disabled={applying} className="bg-pink-600 hover:bg-pink-700 gap-1" data-testid="mlp-apply-btn">
              <Calendar className="w-3.5 h-3.5" />
              {applying ? 'Applying...' : 'Apply MLP'}
            </Button>
          </div>
        ) : (
          <p className="text-xs text-slate-400 mb-4 italic">You have used your MLP leave for this month.</p>
        )}

        {records.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-2">Your MLP History</p>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Applied On</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm">{r.date}</TableCell>
                    <TableCell><Badge className="bg-emerald-100 text-emerald-700 text-xs">Auto-approved</Badge></TableCell>
                    <TableCell className="text-xs text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '--'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function MLPReportTable() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const admin = isAdmin() || isHR();

  useEffect(() => {
    if (!admin) { setLoading(false); return; }
    api.get('/api/leave/mlp/records').then(r => setRecords(r.data || [])).catch(console.error).finally(() => setLoading(false));
  }, [admin]);

  if (!admin) return null;
  if (loading) return <Skeleton className="h-40" />;

  const byMonth = {};
  records.forEach(r => {
    const month = r.date?.substring(0, 7) || 'Unknown';
    if (!byMonth[month]) byMonth[month] = [];
    byMonth[month].push(r);
  });

  return (
    <Card className="border-slate-200 mt-4" data-testid="mlp-report-table">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-pink-500" />
          <CardTitle className="text-sm font-semibold">MLP Records</CardTitle>
          <Badge className="ml-auto bg-pink-100 text-pink-700 text-xs">{records.length} total</Badge>
        </div>
        <p className="text-xs text-slate-400 mt-1">Menstrual Leave Policy usage across the organization (Admin/HR only)</p>
      </CardHeader>
      <CardContent className="pt-2">
        {records.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-6">No MLP records yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Employee</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="text-sm font-medium">{r.employee_name}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{r.employee_code}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.department}</TableCell>
                  <TableCell className="text-sm">{r.date}</TableCell>
                  <TableCell><Badge className="bg-emerald-100 text-emerald-700 text-xs"><Check className="w-3 h-3 mr-0.5 inline" />Auto-approved</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
